import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { trimText } from "shared/utils/utils";
import { BREADCRUMB_DYNAMIC_CONFIG, HOME } from "./config";
import { LeftArrow } from "shared/assets/icons";

import './Breadcrumbs.scss';

export const resetBreadcrumbs = () => {
    sessionStorage.setItem("breadcrumbs", JSON.stringify([HOME]));
};

const setSessionBreadcrumbs = (data) => {
    sessionStorage.setItem("breadcrumbs", JSON.stringify(data));
};

const getSessionBreadcrumbs = () => {
    const sessionBreadcrumbs = sessionStorage.getItem("breadcrumbs");
    if ( typeof sessionBreadcrumbs === 'string' ) {
        return JSON.parse(sessionBreadcrumbs);
    } else {
        return [HOME];
    }
};

const findPathIndex = (breadcrumbs, url, partialMatchPath=null) => {
    const index = breadcrumbs.findIndex(elem => {
        if ( elem.path === url ) {
            return true;
        } else if ( Array.isArray(elem.addOnBreadcrumbs) && elem.addOnBreadcrumbs.length > 0 && ( partialMatchPath ? elem.addOnBreadcrumbs[0].path.startsWith(partialMatchPath) : elem.addOnBreadcrumbs[0].path === url ) ) {
            return true;
        }

        return false;
    });

    return index;
};

export const navigateUsingBreadcrumbs = (history, url, partialMatchPath=null) => {
    const sessionBreadcrumbs = getSessionBreadcrumbs();
    const index = findPathIndex(sessionBreadcrumbs, url, partialMatchPath);
    if ( index < 0 ) {
        history.push(url);
    } else {
        const pagesToGoBack = (index - sessionBreadcrumbs.length) + 1;
        if (pagesToGoBack < 0) {
            history.go(pagesToGoBack);

            setSessionBreadcrumbs(sessionBreadcrumbs.slice(0, index + 1));
        }
    }
};

export const BreadcrumbsManager = (props) => {

    const currentPath = useSelector(state => state.currentPath);
    
    useEffect(() => {
        // Scenario - When full page is refreshed.

        // Getting session breadcrumbs
        const sessionBreadcrumbs = getSessionBreadcrumbs();

        // Finding is the current path is already available in session breadcrumbs
        const index = sessionBreadcrumbs.findIndex(elem => elem.path === props.location.pathname);

        // If the current path exist and it is not the last path. Then re-setting the breadcrumbs.
        if ( index > -1 && props.location.pathname !== sessionBreadcrumbs[sessionBreadcrumbs.length - 1].path ) {
            resetBreadcrumbs();
        }
    }, []);

    useEffect(() => {
        // Scenario - When the navigation hits the home page.
        if ( currentPath === '/' ) {
            resetBreadcrumbs();
        }
    }, [currentPath]);

    return null;
};

export const Breadcrumbs = ({ props, type, options, mobileBack=false, style }) => {
    
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    useEffect(() => {
        if ( type && type in BREADCRUMB_DYNAMIC_CONFIG ) {
            let sessionBreadcrumbs = getSessionBreadcrumbs();

            const index = findPathIndex(sessionBreadcrumbs, props.location.pathname);

            if ( index < 0 ) {

                // Scenario - This will remove the last breadcrumb.
                if ( props.history.action === "REPLACE" ) {
                    sessionBreadcrumbs.splice(sessionBreadcrumbs.length - 1, 1);
                }

                let data = BREADCRUMB_DYNAMIC_CONFIG[type](options);
                if ( 'addOnBreadcrumbs' in data ) {
                    data.addOnBreadcrumbs.forEach(elem => elem.path = props.location.pathname);
                } else {
                    data.path = props.location.pathname;
                }

                sessionBreadcrumbs.push(data);
            } else {
                // Scenario - If the breadcrumb path is already present, then removing the other breadcrumbs after that.
                sessionBreadcrumbs = sessionBreadcrumbs.slice(0, index + 1);
            }

            setBreadcrumbs(sessionBreadcrumbs);
            setSessionBreadcrumbs(sessionBreadcrumbs);
        }
    }, [type]);

    const onClickLink = (item, index) => {
        if (item.path === '/') {
            props.history.push('/');
            resetBreadcrumbs();
        } else {
            const pagesToGoBack = (index - breadcrumbs.length) + 1;
            if (pagesToGoBack < 0) {
                props.history.go(pagesToGoBack);

                const sessionBreadcrumbs = getSessionBreadcrumbs();
                setSessionBreadcrumbs(sessionBreadcrumbs.slice(0, index));
            }
        }
    };

    const onClickBack = () => {
        props.history.go(-1);
    };

    const renderItem = (item, index) => {
        return (
            <>
                <div key={"breadcrumbs-" + index}  className={`link ${index === breadcrumbs.length - 1 ? 'active' : ''}`} aria-label={item.title} title={item.title} onClick={() => { onClickLink(item, index); }}>{trimText(item.title, 50)}</div>
                { 
                    index != breadcrumbs.length - 1 ? 
                    <div key={"breadcrumbs-slash" + index} className="slash">
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.72088 0.79084C8.02048 1.00485 8.08988 1.42121 7.87587 1.72082L1.20921 11.0542C0.995201 11.3538 0.578835 11.4232 0.279226 11.2092C-0.020382 10.9951 -0.0897765 10.5788 0.124229 10.2792L6.7909 0.945837C7.0049 0.646229 7.42127 0.576834 7.72088 0.79084Z" fill="#868E96"/>
                        </svg>
                    </div>  : ''
                }
            </>
        )
    };

    const renderSubItem = (items, item, index, subIndex) => {
        return (
            <>
                <div key={"breadcrumbs-" + subIndex}  className={`link ${index === breadcrumbs.length - 1 ? 'active' : ''}`} aria-label={item.title} title={item.title} onClick={() => { onClickLink(item, index); }}>{trimText(item.title, 50)}</div>
                { 
                    subIndex != items.length - 1 || index != breadcrumbs.length - 1 ? 
                    <div key={"breadcrumbs-slash" + subIndex} className="slash">
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.72088 0.79084C8.02048 1.00485 8.08988 1.42121 7.87587 1.72082L1.20921 11.0542C0.995201 11.3538 0.578835 11.4232 0.279226 11.2092C-0.020382 10.9951 -0.0897765 10.5788 0.124229 10.2792L6.7909 0.945837C7.0049 0.646229 7.42127 0.576834 7.72088 0.79084Z" fill="#868E96"/>
                        </svg>
                    </div>  : ''
                }
            </>
        )
    };
    
    return (
        <div className="breadcrumbs" style={style}>
            {
                breadcrumbs.map((item, index) => 
                    <>
                        {
                            "addOnBreadcrumbs" in item ? 
                            item['addOnBreadcrumbs'].map((subItem, subIndex) => renderSubItem(item['addOnBreadcrumbs'], subItem, index, subIndex)) :
                            renderItem(item, index)
                        }
                    </>
                )
            }
            {
                Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && mobileBack ? 
                <div className="back" onClick={onClickBack}>
                    {LeftArrow}
                    <span>Back</span>
                </div> : null
            }
        </div>
    )
};